import { useLazyQuery, useMutation } from "@apollo/client/react"
import type { FormikHelpers } from "formik"
import { DateTime } from "luxon"
import { useState } from "react"

import {
  CountFutureReservationsDocument,
  type CountFutureReservationsQuery,
  type CountFutureReservationsQueryVariables,
  UpdateVenueLayoutDocument,
  type UpdateVenueLayoutMutation,
  type UpdateVenueLayoutMutationVariables,
  ReservationDateField,
  type GetVenueFullQuery,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

import type { ConfirmDialogState, FormValues } from "./types"

type VenueType = NonNullable<GetVenueFullQuery["venue"]>
type SetFieldValue = FormikHelpers<FormValues>["setFieldValue"]

const DEFAULT_CONFIRM_DIALOG: ConfirmDialogState = {
  open: false,
  title: "",
  message: "",
  onConfirm: () => {},
}

export function useVenueLayoutState(venue: VenueType, refetch: () => Promise<unknown>) {
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(DEFAULT_CONFIRM_DIALOG)

  const [countReservations] = useLazyQuery<CountFutureReservationsQuery, CountFutureReservationsQueryVariables>(
    CountFutureReservationsDocument,
  )

  const [updateLayout, { loading: saving }] = useMutation<
    UpdateVenueLayoutMutation,
    UpdateVenueLayoutMutationVariables
  >(UpdateVenueLayoutDocument)

  const countFutureReservations = async (tableIds: string[]) => {
    setError(null)

    try {
      const { data } = await countReservations({
        variables: {
          slug: venue.slug,
          tableIds,
          dateFilter: { field: ReservationDateField.ReservationDate, from: DateTime.now().toISODate() },
        },
      })

      return data?.venue?.reservations?.totalCount || 0
    } catch (err) {
      setError(i18n.t("app.settings.venues.venue_layout.save_error"))
      showErrorToast(i18n.t("app.settings.venues.venue_layout.save_error"))
      console.error(err)
      return null
    }
  }

  const handleRemoveTableGroup = async (groupIndex: number, values: FormValues, setFieldValue: SetFieldValue) => {
    const group = values.tableGroups[groupIndex]

    if (group.isNew) {
      void setFieldValue(
        "tableGroups",
        values.tableGroups.filter((_, i) => i !== groupIndex),
      )
      return
    }

    const tableIds = group.tables.filter((t) => !t.deleted && t.id).map((t) => t.id)

    if (tableIds.length > 0) {
      const count = await countFutureReservations(tableIds)

      if (count === null) {
        return
      }

      if (count > 0) {
        setConfirmDialog({
          open: true,
          title: i18n.t("app.settings.venues.venue_layout.table_group_name"),
          message: i18n.t("app.settings.venues.venue_layout.confirm_remove_table_group", { count }),
          onConfirm: () => {
            void setFieldValue(
              "tableGroups",
              values.tableGroups.filter((_, i) => i !== groupIndex),
            )
            setConfirmDialog({ ...DEFAULT_CONFIRM_DIALOG })
          },
        })
        return
      }
    }

    void setFieldValue(
      "tableGroups",
      values.tableGroups.filter((_, i) => i !== groupIndex),
    )
  }

  const handleRemoveTable = async (
    groupIndex: number,
    tableIndex: number,
    values: FormValues,
    setFieldValue: SetFieldValue,
  ) => {
    const table = values.tableGroups[groupIndex].tables[tableIndex]

    if (table.isNew) {
      void setFieldValue(
        `tableGroups[${groupIndex}].tables`,
        values.tableGroups[groupIndex].tables.filter((_, i) => i !== tableIndex),
      )
      return
    }

    if (table.id) {
      const count = await countFutureReservations([table.id])

      if (count === null) {
        return
      }

      if (count > 0) {
        setConfirmDialog({
          open: true,
          title: table.name,
          message: i18n.t("app.settings.venues.venue_layout.confirm_remove_table", { count }),
          onConfirm: () => {
            void setFieldValue(
              `tableGroups[${groupIndex}].tables`,
              values.tableGroups[groupIndex].tables.filter((_, i) => i !== tableIndex),
            )
            setConfirmDialog({ ...DEFAULT_CONFIRM_DIALOG })
          },
        })
        return
      }
    }

    void setFieldValue(
      `tableGroups[${groupIndex}].tables`,
      values.tableGroups[groupIndex].tables.filter((_, i) => i !== tableIndex),
    )
  }

  const handleAddTableGroup = (values: FormValues, setFieldValue: SetFieldValue) => {
    void setFieldValue("tableGroups", [
      ...values.tableGroups,
      { name: i18n.t("app.settings.venues.venue_layout.new_table_group"), tables: [], isNew: true, deleted: false },
    ])
  }

  const handleAddTable = (groupIndex: number, values: FormValues, setFieldValue: SetFieldValue) => {
    void setFieldValue(`tableGroups[${groupIndex}].tables`, [
      ...values.tableGroups[groupIndex].tables,
      {
        name: i18n.t("app.settings.venues.venue_layout.new_table", {
          count: values.tableGroups[groupIndex].tables.length + 1,
        }),
        isNew: true,
        isEditing: true,
        deleted: false,
        minCapacity: null,
        maxCapacity: null,
      },
    ])
  }

  const handleSubmit = async (values: FormValues) => {
    setError(null)
    setSuccess(null)

    const variables: UpdateVenueLayoutMutationVariables = {
      venueId: venue.id,
      tableGroups: values.tableGroups.map((tg) => ({
        id: tg.isNew ? null : tg.id,
        name: tg.name,
        tables: tg.tables.map((t) => ({
          id: t.isNew ? null : t.id,
          name: t.name,
          minCapacity: t.minCapacity || null,
          maxCapacity: t.maxCapacity || null,
        })),
      })),
      plans:
        values.plans.length > 0
          ? values.plans.map((m) => ({
              id: m.isNew ? null : m.id,
              sectionName: m.sectionName,
              plan: m.plan,
              position: m.position,
              default: m.default,
            }))
          : null,
    }

    try {
      const result = await updateLayout({ variables })

      if (result.data?.updateVenueLayout?.success) {
        setSuccess(i18n.t("app.settings.venues.venue_layout.save_success"))
        await refetch()
      } else {
        const errorMessages = result.data?.updateVenueLayout?.errors?.map((e) => e.message).join(", ")
        setError(errorMessages || i18n.t("app.settings.venues.venue_layout.save_error"))
      }
    } catch (err) {
      setError(i18n.t("app.settings.venues.venue_layout.save_error"))
      showErrorToast(i18n.t("app.settings.venues.venue_layout.save_error"))
      console.error(err)
    }
  }

  return {
    error,
    success,
    saving,
    confirmDialog,
    setConfirmDialog,
    setError,
    handleRemoveTableGroup,
    handleRemoveTable,
    handleAddTableGroup,
    handleAddTable,
    handleSubmit,
  }
}
